import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface InsertTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsert: (rows: number, cols: number) => void;
}

const MIN_SIZE = 1;
const MAX_SIZE = 20;

export default function InsertTableDialog({ open, onOpenChange, onInsert }: InsertTableDialogProps) {
  const [rows, setRows] = useState("3");
  const [cols, setCols] = useState("3");

  const clamp = (value: number) => Math.min(MAX_SIZE, Math.max(MIN_SIZE, value));

  const handleInsert = () => {
    const parsedRows = clamp(parseInt(rows, 10) || MIN_SIZE);
    const parsedCols = clamp(parseInt(cols, 10) || MIN_SIZE);
    onInsert(parsedRows, parsedCols);
    onOpenChange(false);
    setRows("3");
    setCols("3");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-insert-table">
        <DialogHeader>
          <DialogTitle>Insert Table</DialogTitle>
          <DialogDescription>
            Choose how many rows and columns to start with. You can add more later by pressing Tab in the last cell.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="table-rows">Rows</Label>
            <Input
              id="table-rows"
              type="number"
              min={MIN_SIZE}
              max={MAX_SIZE}
              value={rows}
              onChange={(e) => setRows(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInsert();
              }}
              data-testid="input-table-rows"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="table-cols">Columns</Label>
            <Input
              id="table-cols"
              type="number"
              min={MIN_SIZE}
              max={MAX_SIZE}
              value={cols}
              onChange={(e) => setCols(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInsert();
              }}
              data-testid="input-table-cols"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-insert-table">
            Cancel
          </Button>
          <Button onClick={handleInsert} data-testid="button-confirm-insert-table">
            Insert Table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
